<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import sectionSorter from '@@/bits/section_sorter'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Id } from '@@/types'
import SurfacePostsDraggablePad from '@@/vuecomponents/SurfacePostsDraggablePad.vue'
import SurfacePostsMatrixSection from '@@/vuecomponents/SurfacePostsMatrixSection.vue'
import { useScrollToNewestPost } from '@@/vuecomposables/surface_scroll_to_newest_post'
import { useSurfaceSectionsDragSortable } from '@@/vuecomposables/surface_sections_drag_sortable'
import { storeToRefs } from 'pinia'
import type Vue from 'vue'
import type { Ref } from 'vue'
import { ref } from 'vue'

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
    xSectionHeader?: boolean
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
    xSectionHeader: true,
  },
)

const surfacePostsStore = useSurfacePostsStore()
const surfaceSectionsStore = useSurfaceSectionsStore()
const { dir, isPostSizeStandard, isPostSizeWide } = storeToRefs(useSurfaceStore())
const { isASectionDragging } = storeToRefs(surfaceSectionsStore)
const { sortedGroups } = storeToRefs(useSurfaceWishArrangementStore())

const { shouldDisplayGroup } = surfacePostsStore

const rootElement: Ref<HTMLDivElement | null> = ref(null)

if (!isAppUsing('pdndSurfaceMatrix')) {
  useSurfaceSectionsDragSortable({
    onDragStart: () => {
      isASectionDragging.value = true
    },
    onDragEnd: ({ groupId }) => {
      isASectionDragging.value = false
      if (groupId) {
        const sectionElement = document.querySelector(`#section-${groupId}`) as HTMLElement | undefined
        if (!sectionElement) return
        const sortResult = sectionSorter.getNewIndex(sectionElement)
        if (!sortResult) return
        surfaceSectionsStore.sortSection(sortResult)
      }
    },
  })
}

useScrollToNewestPost(rootElement)

function assignRootElementRef(v: Vue | null): void {
  rootElement.value = v?.$el as HTMLDivElement | null
}
</script>

<template>
  <SurfacePostsDraggablePad
    :ref="assignRootElementRef"
    :dir="dir"
    :class="[
      'flex',
      'flex-col',
      'items-center',
      'space-y-8 @tablet-portrait/surface-container:space-y-12',
      'mx-auto',
      'box-border',
      'w-full',
      // account for gaps (when there are 2 posts 1 gap between posts, when there are 4 posts from 3 gaps between posts).
      // 1136px = 1024px + 48px post gaps + 48px horizontal padding + 16px scrollbar
      // 1184px = 1024px + 48px post gaps + 96px horizontal padding + 16px scrollbar
      // 1088px = 1024px + 48px post gaps + 16px scrollbar
      isPostSizeStandard &&
        '@tablet-portrait/surface-container:max-w-[1136px] @tablet-landscape/surface-container:max-w-[1184px] @desktop/surface-container:max-w-[1088px] @tablet-portrait/surface-container:px-6 @tablet-landscape/surface-container:px-12 @desktop/surface-container:px-0',
      // on tablet-portrait and desktop on wide post size we need to account for horizontal margins in between posts for correct number of posts fit in each row
      // since we are using border-box
      // for tablet-portrait 1140px + 2*16px (gap since there are 3 posts) + 48px horizontal padding + 16px scrollbar = 1236px
      // for desktop 1520px + 3*16px (gap since there are 4 posts) + 48px*2 horizontal padding +16px scrollbar = 1680px
      isPostSizeWide &&
        '@tablet-portrait/surface-container:max-w-[1236px] @desktop/surface-container:max-w-[1680px] @tablet-portrait/surface-container:px-6 @desktop/surface-container:px-12',
      'py-4',
    ]"
  >
    <SurfacePostsMatrixSection
      v-for="group in sortedGroups"
      v-show="shouldDisplayGroup(group)"
      :key="group.id"
      :group="group"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
      :x-section-header="xSectionHeader"
    />

    <!-- This should stay under the same parent as the posts for sizing to work properly because it relies on stuffs like calc(25% - 16px) -->
    <div key="wish-sizer" class="wish-sizer" />
  </SurfacePostsDraggablePad>
</template>
